import type { ReactElement } from 'react'
import type { HabitHeatmapHabit } from '../utils/habitHeatmap'
import {
  type HabitHeatmapLog,
  type HabitHeatmapState,
  getHabitHeatmapWeeks,
} from '../utils/habitHeatmap'

interface HabitHeatmapGridProps {
  habit: HabitHeatmapHabit
  logs: HabitHeatmapLog[]
  selectedDate: Date
  dayStartTime: string
  isLoading?: boolean
  weekCount?: number
}

const STATE_CLASSES: Record<HabitHeatmapState, string> = {
  completed: 'bg-emerald-500 ring-emerald-600',
  skipped: 'bg-amber-300 ring-amber-400',
  empty: 'bg-gray-100 ring-gray-200',
}

const LOADING_CELL_KEYS = Array.from(
  { length: 84 },
  (_, index) => `loading-cell-${index}`,
)

function getStateLabel(state: HabitHeatmapState): string {
  if (state === 'completed') return 'Completed'
  if (state === 'skipped') return 'Skipped'
  return 'Empty'
}

export function HabitHeatmapGrid({
  habit,
  logs,
  selectedDate,
  dayStartTime,
  isLoading = false,
  weekCount = 12,
}: HabitHeatmapGridProps): ReactElement {
  const weeks = getHabitHeatmapWeeks(
    habit,
    logs,
    selectedDate,
    dayStartTime,
    weekCount,
  )

  if (isLoading) {
    return (
      <div className="grid grid-cols-12 gap-1">
        {LOADING_CELL_KEYS.map((key) => (
          <div
            key={key}
            className="aspect-square animate-pulse rounded bg-gray-100"
          />
        ))}
      </div>
    )
  }

  return (
    <div
      className="grid gap-1"
      style={{
        gridTemplateColumns: `repeat(${weeks.length}, minmax(0, 1fr))`,
      }}
    >
      {weeks.map((week) => (
        <div key={week.weeklyKey} className="flex flex-col gap-1">
          {week.days.map((day) => (
            <span
              key={day.dailyKey}
              title={`${day.dailyKey}: ${getStateLabel(day.state)}`}
              className={`aspect-square rounded ring-1 ${STATE_CLASSES[day.state]}`}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

export function HabitHeatmapLegend(): ReactElement {
  return (
    <div className="flex items-center justify-between gap-3 text-gray-500 text-xs">
      <div className="flex items-center gap-2">
        <span className="h-3 w-3 rounded bg-gray-100 ring-1 ring-gray-200" />
        <span>Empty</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="h-3 w-3 rounded bg-amber-300 ring-1 ring-amber-400" />
        <span>Skipped</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="h-3 w-3 rounded bg-emerald-500 ring-1 ring-emerald-600" />
        <span>Completed</span>
      </div>
    </div>
  )
}
